import { DataField } from "@iventis/domain-model/model/dataField";
import { DataFieldType } from "@iventis/domain-model/model/dataFieldType";
import { ScheduleConfigVersionType } from "@iventis/domain-model/model/scheduleConfigVersionType";
import { Content } from "@iventis/translations";
import { EMPTY_GUID } from "@iventis/utilities";
import { v4 as uuid } from "uuid";

export const getScheduleVersionTitle = (index: number, translate: (key: string) => string) => {
    switch (index) {
        case 0:
            return translate(Content.settings.scheduleConfig.scheduleVersionTopLevelTitle);
        case 1:
            return translate(Content.settings.scheduleConfig.scheduleVersionSecondLevelTitle);
        default:
            return `${index} ${translate(Content.settings.scheduleConfig.eventBlockTitleDefault)}`;
    }
};

export const getScheduleVersionDataFields = (scheduleDataFields: DataField[], dataFieldIds: string[]) => scheduleDataFields.filter(({ id }) => dataFieldIds.includes(id));

/** Orders schedule version types by the allowedParentType property */
export const orderScheduleVersions = (scheduleVersionTypes: ScheduleConfigVersionType[]): ScheduleConfigVersionType[] => {
    const topLevelScheduleVersion = findScheduleVersionType(scheduleVersionTypes, "allowedParentType", EMPTY_GUID);

    if (!topLevelScheduleVersion) {
        throw new Error("Top level schedule version type could not be found");
    }

    return findNextLevelScheduleVerionType(scheduleVersionTypes, topLevelScheduleVersion);
};

const findNextLevelScheduleVerionType = (
    scheduleVersions: ScheduleConfigVersionType[],
    scheduleVersion: ScheduleConfigVersionType,
    orderScheduleVersions: ScheduleConfigVersionType[] = []
) => {
    const updatedOrder = [...orderScheduleVersions, scheduleVersion];
    const found = findScheduleVersionType(scheduleVersions, "allowedParentType", scheduleVersion.id);
    if (!found) {
        return updatedOrder;
    }
    return findNextLevelScheduleVerionType(scheduleVersions, found, updatedOrder);
};

const findScheduleVersionType = <PropertyType extends keyof ScheduleConfigVersionType>(
    scheduleVersions: ScheduleConfigVersionType[],
    property: PropertyType,
    value: ScheduleConfigVersionType[PropertyType]
) => scheduleVersions.find((type) => type[property] === value);

export const createDefaultScheduleVersionTypes = (disciplineDataFieldId: string): ScheduleConfigVersionType[] => {
    const topLevelScheduleVersionId = uuid();

    return [
        {
            id: topLevelScheduleVersionId,
            name: "Schedule",
            dataFields: [],
            allowedParentType: EMPTY_GUID,
        },
        {
            id: uuid(),
            name: "Discipline schedule",
            dataFields: [disciplineDataFieldId],
            allowedParentType: topLevelScheduleVersionId,
        },
    ];
};

export const createDiscplineDataField = (): DataField => ({
    id: uuid(),
    name: "Discipline",
    allowedMetadataProperties: ["code"],
    customerIdentifierName: "code",
    apiNamePlural: "disciplines",
    apiNameSingular: "discipline",
    type: DataFieldType.List,
    listValues: [],
    showInApi: false,
    projectId: undefined,
    hierarchyMetadataPropertyName: undefined,
    parentDataFieldId: undefined,
    listItemProperties: [],
    listItemRelationships: [],
    order: 0,
    deprecated: false,
    defaultValue: null,
});
